/**
 * Reusable search bar: no Redux, no useTools. Uses useState and optional ref methods.
 *
 * Parent can bind in two ways:
 * 1. Controlled: pass value + onChange (+ onSearch). Parent owns state.
 * 2. Uncontrolled: pass defaultValue only. Component owns state; use ref to read/set.
 *
 * Ref methods (when parent passes ref): getValue(), setValue(v), clear(), focus().
 *
 * Use anywhere: Teams page, All Documents, or other screens.
 */
import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useSearchPanel } from "../../theme/styles/SearchPanelStyle";
import { useTheme } from "../../theme/OAS365Theme";
import searchIcon from "../../assets/icons/search.png";

const SearchBar = forwardRef(function SearchBar(
  {
    value: controlledValue,
    defaultValue = "",
    onChange,
    onSearch,
    onClearAndSearch,
    placeholder = "Search for documents",
    disabled = false,
    loading = false,
    showFilterButton = false,
    isFilterVisible = false,
    onFilterVisibleChange,
    filterContent = null,
  },
  ref,
) {
  const theme = useTheme();
  const searchPanelStyle = useSearchPanel();
  const inputRef = useRef(null);

  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? (controlledValue ?? "") : uncontrolledValue;

  const setValue = (next) => {
    if (!isControlled) setUncontrolledValue(next ?? "");
    onChange?.(next ?? "");
  };

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => value,
      setValue: (v) => setValue(v),
      clear: () => setValue(""),
      focus: () => inputRef.current?.focus(),
    }),
    [value, isControlled, onChange],
  );

  const isClearDisabled = value === undefined || value === null || value === "";

  const handleClear = () => {
    if (typeof onClearAndSearch === "function") {
      onClearAndSearch();
      return;
    }
    setValue("");
    onSearch?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") onSearch?.();
  };

  const handleFilterClick = () => {
    if (loading) return;
    onFilterVisibleChange?.(!isFilterVisible);
  };

  return (
    <Stack
      horizontal
      wrap
      verticalAlign="center"
      tokens={{ childrenGap: 8 }}
      styles={{
        root: {
          padding: "8px 0",
          background: theme.colors.backgroundColor,
          flexWrap: "wrap",
          rowGap: "8px",
          alignItems: "center",
        },
      }}
    >
      <Stack
        horizontal
        styles={{
          root: {
            flex: "1 1 200px",
            minWidth: 200,
            maxWidth: 300,
            display: "flex",
            ...(isSearchFocused
              ? searchPanelStyle.ToolBarStylesHighlight?.root
              : searchPanelStyle.ToolBarStyles?.root),
          },
        }}
      >
        <input
          ref={inputRef}
          type="text"
          style={{
            ...searchPanelStyle.ToolBarSearchTextBoxStyle,
            flex: 1,
            minWidth: 0,
          }}
          placeholder={placeholder}
          value={value}
          disabled={disabled || loading}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          onChange={(ev) => setValue(ev.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search documents"
        />
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <IconButton
            styles={{
              root: isClearDisabled
                ? searchPanelStyle.StackSearchClearButtonToolBarDisableStyles
                : searchPanelStyle.StackSearchClearButtonToolBarStyles,
            }}
            disabled={isClearDisabled}
            iconProps={{ iconName: "Clear" }}
            onClick={handleClear}
            ariaLabel="Clear search"
          />
          <div
            style={{ width: "2px", background: theme.colors.Addovation20Grey }}
          />
          <IconButton
            styles={{
              root: {
                ...(loading
                  ? searchPanelStyle.SearchButtonToolBarDisabledStyle
                  : searchPanelStyle.SearchButtonToolBarStyle),
                
              },
              icon: {
                fontSize: "12px",
              },
            }}
            disabled={loading}
            iconProps={{
              imageProps: { src: searchIcon, width: 18, height: 18, alt: "Search Icon" },
              imageSize: 24,
            }}
            onClick={() => onSearch?.()}
            ariaLabel="Search"
          />
        </Stack>
      </Stack>

      {showFilterButton && (
        <>
          <div
            style={{
              ...searchPanelStyle.FilterButtonContainerStyle,
              ...(loading && { pointerEvents: "none", opacity: 0.6 }),
            }}
            onClick={handleFilterClick}
            role="button"
            tabIndex={loading ? -1 : 0}
            onKeyDown={(e) =>
              !loading && e.key === "Enter" && handleFilterClick()
            }
            aria-disabled={loading}
          >
            <button
              type="button"
              disabled={loading}
              onClick={handleFilterClick}
              style={searchPanelStyle.FilterButtonStyle}
            >
              {isFilterVisible ? "Filters <<" : "Filters >>"}
            </button>
          </div>
          {isFilterVisible && filterContent}
        </>
      )}
    </Stack>
  );
});

export default SearchBar;
